import request from '@/util/request'
import {createHelpers} from 'vuex-map-fields'
import {isEqual} from 'lodash'

const {getConfig} = createHelpers({
  getterType: 'getConfig',
})

const state = {
  TRADING_DISABLED: null,
  ALERT_BANNER_ENABLED: null,
  ALERT_BANNER_MESSAGE: null,
  ALERT_BANNER_URL: null,
  ALERT_BANNER_URL_NEW_WINDOW: true,
  REFINERY_BANNER_ENABLED: null,
  REFINERY_BANNER_MESSAGE: null,
  FEDEX_SHIPSECURE_ENABLED: null,
  UPS_SHIPSECURE_ENABLED: null,
  NS_SYNC_ENABLED: null,
  NETSUITE_APP_URL: null,
  BUY_LIMIT_THRESHOLD: null,
  SELL_LIMIT_THRESHOLD: null,
  PAY_NOW_ENABLED: null,
  MASTER_LOTS_ENABLED: null,
  REFINING_LOT_WEIGHT_VARIANCE_PERCENT: null,
  UNREAD_ALERTS_COUNT: 0,
  UNREAD_ALERTS_ROWS: [],
  ACTIVE_REMINDER_IDS: [],
  RECENTLY_DISMISSED_REMINDER_IDS: [],
  COUNTRIES: {},
  SUITEANALYTICS_CONNECT_CREDENTIALS: {},
}

const getters = {
  getConfig,
  getActiveReminderIds: (state) => {
    // get active reminder ids, removing recently dismissed ids
    return state.ACTIVE_REMINDER_IDS.filter((id) => {
      return !state.RECENTLY_DISMISSED_REMINDER_IDS.includes(id)
    })
  },
  getSuiteAnalyticsConnectCredentials: (state) => {
    return state.SUITEANALYTICS_CONNECT_CREDENTIALS
  }
}

const actions = {
  fetchSuiteAnalyticsConnectCredentials({commit, getters}) {
    return request({
      url: `/configurations/suiteanalytics-connect`,
      method: 'get',
    }).then((data) => {
      commit('SET_SUITEANALYTICS_CONNECT_CREDENTIALS', data)
    })
  },
  fetchReminder({commit, getters}, {id}) {
    return request({
      url: `/reminders/read/${id}?companyId=${getters.getSelectedCompany.id}`,
      method: 'get',
    })
  },
  markReminderAsRead({commit, getters}, {id, read = 0, actionCompletedAt}) {
    return request({
      url: `/reminders/read/${id}/${read}?companyId=${getters.getSelectedCompany.id}`,
      data: {actionCompletedAt},
      method: 'patch',
    }).then(() => {
      //add id to recently dismissed
      commit('ADD_RECENTLY_DISMISSED_REMINDER_ID', id)
    })
  },
  fetchCountries({commit, getters}) {
    return request({
      url: `/configurations/countries?companyId=${getters.getSelectedCompany.id}`,
      method: 'get',
    }).then((data) => {
      commit('SET_COUNTRIES', data)
    })
  },
  fetchConfig({commit, getters}) {
    // allow fetching baseline config even if selected company is not yet set
    // (needed so client can receive LOGZIO_TOKEN early for analytics init)
    const companyId = getters.getSelectedCompany && getters.getSelectedCompany.id
    return request({
      url: companyId
        ? `/configurations?companyId=${companyId}&limit=1000`
        : `/configurations?limit=1000`,
      method: 'get',
    }).then((data) => {
      commit('SET_CONFIGURATION', data.data.rows)
    })
  },
  updateConfig({commit, dispatch}, {key, value}) {
    return request({
      url: '/configurations',
      method: 'post',
      data: {key, value},
    }).then(() => {
      return dispatch('fetchConfig', {commit})
    })
  },
  fetchAllWarehouseLocations() {
    return request({
      url: '/warehouse-locations',
      method: 'get',
    })
  },
  updateDefaultShipToWarehouse(context, {defaultWarehouse}) {
    return request({
      url: '/warehouse-locations/default-priority',
      method: 'post',
      data: {defaultWarehouse},
    })
  },
}

const mutations = {
  SET_SUITEANALYTICS_CONNECT_CREDENTIALS(state, value) {
    state.SUITEANALYTICS_CONNECT_CREDENTIALS = value
  },
  ADD_RECENTLY_DISMISSED_REMINDER_ID(state, id) {
    state.RECENTLY_DISMISSED_REMINDER_IDS.push(id)
    // Only keep the last 25 dismissed reminders
    if (state.RECENTLY_DISMISSED_REMINDER_IDS.length > 25) {
      state.RECENTLY_DISMISSED_REMINDER_IDS.shift()
    }
  },
  SET_COUNTRIES(state, value) {
    state.COUNTRIES = value
  },
  SET_CONFIGURATION(state, configRows) {
    const newConfig = {}
    let activeReminderIds = []
    for (let i in configRows) {
      let value = configRows[i].value
      let key = configRows[i].key
      switch (value) {
        case `true`:
          value = true
          break
        case `false`:
          value = false
          break
      }
      switch (key) {
        case `UNREAD_ALERTS_ROWS`:
          newConfig[key] = JSON.parse(value)
          break
        case `ACTIVE_REMINDER_IDS`:
          activeReminderIds = ((value.indexOf(`,`) > -1) ? value.split(`,`) : [value]).filter(x => Boolean(x));
          // Only overwrite the stored value if the new value is different
          if (!isEqual(activeReminderIds, state.ACTIVE_REMINDER_IDS)) newConfig[key] = activeReminderIds
          break;
        case `EXPERIMENTS`:
          // The mutation for setting experiments/registered experiments is in the experiments module
          this.commit(`SET_EXPERIMENTS`, value)
          break;
        default:
          newConfig[key] = value
          break
      }
    }
    newConfig['CLIENT_TIMESTAMP'] = new Date().toISOString()
    newConfig['SERVER_CLIENT_TIMESTAMP_DIFFERENCE'] =
      (new Date(newConfig['CLIENT_TIMESTAMP']).getTime() -
        new Date(newConfig['SERVER_TIMESTAMP']).getTime()) /
      1000
    Object.assign(state, newConfig)
  },
}

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations
}